import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useIsMobile } from '@/app/useIsMobile';
import { useStaffAuth } from '@/features/auth/useStaffAuth';
import { DeviceHistory } from '@/features/inventory/DeviceDialogs';
import { Dialog, Pill, SECONDARY_BUTTON, formatInstant } from '@/features/inventory/registerUi';
import { useAdminLive, type Motion } from '@/features/live/useAdminLive';
import { InstallationPanel } from '@/features/installations/InstallationPanel';
import { GoLivePanel } from '@/features/provisioning/GoLivePanel';
import {
  isCustomerVisible,
  isHidden,
  useAdminVehicles,
  type AdminVehicle,
} from '@/features/vehicles/useAdminVehicles';
import { UnassignDeviceDialog } from '@/features/vehicles/UnassignDeviceDialog';
import { EditVehicleDialog } from '@/features/vehicles/EditVehicleDialog';

/**
 * EVERY VEHICLE ON THE PLATFORM (slice 18f).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE HEADER IS THE FEATURE. Mahad: *"I don't see how many vehicles
 * registered."* and *"I want as admin to see all what are under my watch —
 * tenant, vehicle assigned to tenant, devices, simcards."*
 * ─────────────────────────────────────────────────────────────────────────────
 * `N vehicles · M live · K awaiting go-live` is the direct answer, and all three
 * figures are counted from THE ROWS THIS SCREEN IS SHOWING — never from a
 * separate count query, which can disagree with the list under it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 NO IMEI COLUMN, AND THAT IS A SECURITY PROPERTY, NOT AN OMISSION.
 * ─────────────────────────────────────────────────────────────────────────────
 * `GET /admin/vehicles` is `requireStaff` — a support hire needs the fleet list.
 * Every route that returns an IMEI is `requireSuperAdmin`. The API deliberately
 * leaves the IMEI off this wire so that gate is not decoration, and this screen
 * must not reintroduce it from anywhere else. `deviceId` deep-links to
 * `/devices`, where a super-admin reads it behind the right guard.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LAST FIX AND MOTION COME FROM `/admin/live`, JOINED BY `vehicleId`.
 * ─────────────────────────────────────────────────────────────────────────────
 * They are not on `AdminVehicleZ` and should not be: one is register state and
 * the other is Redis state with a different freshness. Both routes are
 * `requireStaff`. When the live read is DEGRADED (Redis unreachable) the motion
 * column says so rather than printing "stopped" for a fleet nobody can see —
 * the same rule `/live` and `/commissioning` follow, because a blind screen
 * claiming everything is parked is the worst failure any of the three can have.
 *
 * NO ARTBOARD EXISTS FOR THIS SCREEN. Canvas `1k` is the CUSTOMER's fleet table
 * and `1t` is the tenant list; nothing in the 51 draws a cross-tenant vehicle
 * list, because the need surfaced in use rather than in design. The furniture is
 * the shipped admin registers — said plainly rather than approximated from a
 * pixel source that does not exist.
 */

const MOTION_DOT: Record<Motion, string> = {
  moving: 'bg-emerald-500',
  idle: 'bg-amber-500',
  stopped: 'bg-slate-400',
  unknown: 'bg-indigo-500',
  offline: 'bg-slate-500',
};

const TENANT_TONE: Record<AdminVehicle['tenantStatus'], string> = {
  active: 'border-raad-border text-raad-muted',
  past_due: 'border-raad-warn/40 text-raad-warn',
  suspended: 'border-raad-error/40 text-raad-error',
};

/**
 * The install-record chip (slice 30). `no_tracker` shows NOTHING here — the
 * Tracker column already says "no tracker fitted", and a second "no record" pill
 * for a vehicle with nowhere to install would be noise. The other three each get
 * a word an operator can scan for.
 */
const INSTALL_CHIP: Record<
  Exclude<AdminVehicle['installStatus'], 'no_tracker'>,
  { key: string; tone: string }
> = {
  none: { key: 'vehicles.install.none', tone: 'border-raad-warn/40 text-raad-warn' },
  unconfirmed: { key: 'vehicles.install.unconfirmed', tone: 'border-raad-warn/40 text-raad-warn' },
  confirmed: { key: 'vehicles.install.confirmed', tone: 'border-raad-primary/50 text-raad-accent' },
};

/**
 * 🔴 THREE STATES, NOT TWO (18g). "Not live" used to mean everything that was
 * not live, which now silently mixes two different situations: a vehicle nobody
 * has ever cleared, and one a super-admin deliberately WITHDREW from a paying
 * customer. Those need different actions, so they are different filters — and
 * `hidden` is the one an operator goes looking for.
 */
type LiveFilter = 'all' | 'live' | 'not_live' | 'hidden';

/** The last-known live state joined by `vehicleId` (absent = `/live` silent). */
type LiveState = { motion: Motion; fixTime: string | null };

/**
 * THE CELL RENDERERS — ONE SOURCE for the table (md+) AND the mobile card (< md),
 * slice 84b. Each carries a LOAD-BEARING TRUTH STATE (no tracker fitted, the
 * em-dash for "no data", "motion blind" while the live read is degraded, the
 * three go-live states). Duplicating that logic into a card is how one branch
 * silently starts printing `#null` or "stopped" while blind — so it is defined
 * once here and both branches call it.
 */
function InstallChip({ v }: { v: AdminVehicle }) {
  const { t } = useTranslation();
  const chip =
    v.installStatus === 'none' ||
    v.installStatus === 'unconfirmed' ||
    v.installStatus === 'confirmed'
      ? INSTALL_CHIP[v.installStatus]
      : null;
  return chip ? <Pill label={t(chip.key)} tone={chip.tone} /> : null;
}

/** The register row uuid, NEVER the IMEI (see the header). Null = no tracker. */
function TrackerCell({ v, isSuperAdmin }: { v: AdminVehicle; isSuperAdmin: boolean }) {
  const { t } = useTranslation();
  if (v.traccarDeviceId === null && v.deviceId === null) {
    return <Pill label={t('vehicles.noTracker')} tone="border-raad-warn/40 text-raad-warn" />;
  }
  if (v.deviceId !== null && isSuperAdmin) {
    return (
      <Link to="/devices" className="text-raad-accent underline">
        {t('vehicles.openRegister')}
      </Link>
    );
  }
  if (v.traccarDeviceId !== null) {
    return <span className="num text-raad-muted">#{v.traccarDeviceId}</span>;
  }
  return (
    <span className="text-raad-muted" aria-hidden>
      &mdash;
    </span>
  );
}

/** Three go-live states: never cleared · live since · withdrawn (18g). */
function StateCell({ v }: { v: AdminVehicle }) {
  const { t, i18n } = useTranslation();
  if (v.liveAt === null) {
    return <Pill label={t('vehicles.notLive')} tone="border-raad-primary/50 text-raad-accent" />;
  }
  if (v.hiddenAt !== null) {
    return (
      <>
        <Pill label={t('vehicles.hidden')} tone="border-raad-warn/40 text-raad-warn" />
        <span className="mt-1 block text-[11.5px] text-raad-muted">
          {formatInstant(v.hiddenAt, i18n.language)}
        </span>
      </>
    );
  }
  return (
    <span className="text-[12px] text-raad-muted">{formatInstant(v.liveAt, i18n.language)}</span>
  );
}

function LastFixCell({ st }: { st: LiveState | undefined }) {
  const { i18n } = useTranslation();
  return st?.fixTime ? (
    <span className="num text-[12px]">{formatInstant(st.fixTime, i18n.language)}</span>
  ) : (
    <span className="text-raad-muted" aria-hidden>
      —
    </span>
  );
}

function MotionCell({ st, degraded }: { st: LiveState | undefined; degraded: boolean }) {
  const { t } = useTranslation();
  // 🔴 NEVER "stopped" WHILE BLIND.
  if (degraded) return <span className="text-raad-muted">{t('commissioning.motionBlind')}</span>;
  if (st === undefined) {
    return (
      <span className="text-raad-muted" aria-hidden>
        —
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <span className={['size-2 flex-none rounded-full', MOTION_DOT[st.motion]].join(' ')} />
      {t(`live.motion.${st.motion}`)}
    </span>
  );
}

/**
 * The mobile card (< md) — slice 84b. Same data, same cell renderers as the
 * table row. `data-testid` is what the mobile gate keys the reflow assertion on.
 */
function VehicleCard({
  v,
  st,
  degraded,
  isSuperAdmin,
  onOpen,
}: {
  v: AdminVehicle;
  st: LiveState | undefined;
  degraded: boolean;
  isSuperAdmin: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="vehicle-card"
      className="rounded-panel border border-raad-border bg-raad-panel p-4"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <button
          type="button"
          onClick={onOpen}
          className="flex min-h-11 items-center text-left text-raad-accent underline"
        >
          <span className="num font-medium">{v.plate}</span>
        </button>
        {v.label ? <span className="text-[12.5px] text-raad-muted">{v.label}</span> : null}
        <InstallChip v={v} />
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-raad-muted">
        <span className="text-raad-text">{v.tenantName}</span>
        <Pill label={t(`tenants.status.${v.tenantStatus}`)} tone={TENANT_TONE[v.tenantStatus]} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[12px]">
        <div>
          <dt className="text-[10.5px] uppercase tracking-wide text-raad-muted">
            {t('vehicles.col.tracker')}
          </dt>
          <dd className="mt-0.5">
            <TrackerCell v={v} isSuperAdmin={isSuperAdmin} />
          </dd>
        </div>
        <div>
          <dt className="text-[10.5px] uppercase tracking-wide text-raad-muted">
            {t('vehicles.col.state')}
          </dt>
          <dd className="mt-0.5">
            <StateCell v={v} />
          </dd>
        </div>
        <div>
          <dt className="text-[10.5px] uppercase tracking-wide text-raad-muted">
            {t('vehicles.col.lastFix')}
          </dt>
          <dd className="mt-0.5">
            <LastFixCell st={st} />
          </dd>
        </div>
        <div>
          <dt className="text-[10.5px] uppercase tracking-wide text-raad-muted">
            {t('vehicles.col.motion')}
          </dt>
          <dd className="mt-0.5">
            <MotionCell st={st} degraded={degraded} />
          </dd>
        </div>
      </dl>
    </div>
  );
}

export function Vehicles() {
  const { t } = useTranslation();
  const { state } = useStaffAuth();
  const isSuperAdmin = state.status === 'authenticated' && state.user.role === 'super_admin';
  const isMobile = useIsMobile();
  const q = useAdminVehicles();
  const liveQ = useAdminLive();
  const [tenantId, setTenantId] = useState('');
  const [liveFilter, setLiveFilter] = useState<LiveFilter>('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  /** The vehicle whose tracker is being taken out, or null. */
  const [unassigning, setUnassigning] = useState<string | null>(null);
  /** The vehicle whose identity (plate/label/type) is being edited, or null. */
  const [editing, setEditing] = useState<string | null>(null);

  const rows = useMemo(
    () => (Array.isArray(q.data?.vehicles) ? q.data.vehicles : null),
    [q.data],
  );

  /** vehicleId → last-known state. Absent = `/live` has not answered yet. */
  const liveById = useMemo(() => {
    const m = new Map<string, { motion: Motion; fixTime: string | null }>();
    for (const v of liveQ.data?.vehicles ?? []) {
      m.set(v.vehicleId, { motion: v.motion, fixTime: v.fixTime });
    }
    return m;
  }, [liveQ.data]);

  if (q.isPending) {
    // A skeleton and NO COUNTS. "0 vehicles" that becomes "14" a moment later is
    // a claim about the platform that was never true.
    return (
      <section aria-labelledby="vehicles-heading">
        <h1 id="vehicles-heading" className="font-display text-xl font-bold tracking-tight">
          {t('vehicles.title')}
        </h1>
        <div className="mt-4 grid gap-3" role="status" aria-live="polite">
          <span className="sr-only">{t('vehicles.loading')}</span>
          <div className="h-40 animate-pulse rounded-panel border border-raad-border bg-raad-panel" />
        </div>
      </section>
    );
  }

  if (q.isError || rows === null) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('vehicles.error')}
      </p>
    );
  }

  // 🔴 THE FULL VISIBILITY PREDICATE, NOT JUST `liveAt` (18g). A withdrawn
  // vehicle still carries the `liveAt` it was first cleared at — that column is
  // history and is never rewritten — so counting on it alone would tell a
  // super-admin that N customers can see a vehicle when some of them cannot.
  // `isCustomerVisible` is the one derivation, shared with the row pill and the
  // filter below, and it mirrors the server's exported `IS_CUSTOMER_VISIBLE`.
  const live = rows.filter(isCustomerVisible).length;
  const hidden = rows.filter(isHidden).length;
  const awaiting = rows.length - live - hidden;
  const degraded = liveQ.data?.degraded === true;

  const tenants = [...new Map(rows.map((v) => [v.tenantId, v.tenantName])).entries()].sort((a, b) =>
    a[1].localeCompare(b[1]),
  );

  const needle = search.trim().toLowerCase();
  const filtered = rows.filter((v) => {
    if (tenantId !== '' && v.tenantId !== tenantId) return false;
    if (liveFilter === 'live' && !isCustomerVisible(v)) return false;
    // "Not live" is now NEVER-CLEARED only. A withdrawn vehicle has its own
    // filter, because "nobody has released it yet" and "someone took it away
    // from a paying customer" are different problems with different next steps.
    if (liveFilter === 'not_live' && v.liveAt !== null) return false;
    if (liveFilter === 'hidden' && !isHidden(v)) return false;
    if (needle === '') return true;
    return [v.plate, v.label].some((x) => typeof x === 'string' && x.toLowerCase().includes(needle));
  });

  const open = openId === null ? null : (rows.find((v) => v.vehicleId === openId) ?? null);
  const unassignTarget =
    unassigning === null ? null : (rows.find((v) => v.vehicleId === unassigning) ?? null);
  const editTarget =
    editing === null ? null : (rows.find((v) => v.vehicleId === editing) ?? null);

  return (
    <section aria-labelledby="vehicles-heading">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h1 id="vehicles-heading" className="font-display text-xl font-bold tracking-tight">
          {t('vehicles.title')}
        </h1>
        {/* 🔴 THE ANSWER TO "I don't see how many vehicles registered." */}
        <p className="text-sm text-raad-muted">
          {t('vehicles.summary', { count: rows.length })}
          {' · '}
          {t('vehicles.liveCount', { count: live })}
          {' · '}
          {t('vehicles.awaitingCount', { count: awaiting })}
          {/* Only when there IS one. A permanent "0 hidden" is furniture a
              super-admin learns to ignore; appearing the moment it is non-zero
              puts it in front of them exactly when it means something. */}
          {hidden > 0 ? ` · ${t('vehicles.hiddenCount', { count: hidden })}` : ''}
        </p>
      </div>

      {degraded ? (
        <p
          role="alert"
          className="mt-3 rounded-lg border border-raad-warn/40 bg-raad-warn/10 p-3 text-[12.5px]"
        >
          {t('vehicles.degraded')}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <label className="block max-w-xs grow">
          <span className="sr-only">{t('vehicles.search')}</span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('vehicles.search')}
            className="min-h-10 w-full rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          />
        </label>
        <label className="block">
          <span className="sr-only">{t('vehicles.filterTenant')}</span>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            className="min-h-10 rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          >
            <option value="">{t('vehicles.allTenants')}</option>
            {tenants.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="sr-only">{t('vehicles.filterLive')}</span>
          <select
            value={liveFilter}
            onChange={(e) => setLiveFilter(e.target.value as LiveFilter)}
            className="min-h-10 rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
          >
            <option value="all">{t('vehicles.anyState')}</option>
            <option value="live">{t('vehicles.stateLive')}</option>
            <option value="not_live">{t('vehicles.stateNotLive')}</option>
            <option value="hidden">{t('vehicles.stateHidden')}</option>
          </select>
        </label>
      </div>

      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{t('vehicles.empty')}</p>
      ) : filtered.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{t('vehicles.noMatch')}</p>
      ) : isMobile ? (
        /* Mobile (< md): stacked cards — slice 84b. Same rows, same cells. A
           matchMedia single branch (not CSS `hidden`) so only ONE copy of each
           row is in the DOM — see `useIsMobile`. */
        <div className="mt-4 space-y-3">
          {filtered.map((v) => (
            <VehicleCard
              key={v.vehicleId}
              v={v}
              st={liveById.get(v.vehicleId)}
              degraded={degraded}
              isSuperAdmin={isSuperAdmin}
              onOpen={() => setOpenId(v.vehicleId)}
            />
          ))}
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-panel border border-raad-border bg-raad-panel px-4">
          <table className="w-full min-w-[52rem] text-sm">
            <thead>
              <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('vehicles.col.vehicle')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('vehicles.col.tenant')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('vehicles.col.tracker')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('vehicles.col.state')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('vehicles.col.lastFix')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('vehicles.col.motion')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => {
                const st = liveById.get(v.vehicleId);
                return (
                  <tr key={v.vehicleId} className="border-b border-raad-border last:border-b-0">
                    <td className="py-3 pr-4 align-top">
                      <button
                        type="button"
                        onClick={() => setOpenId(v.vehicleId)}
                        className="text-left text-raad-accent underline"
                      >
                        <span className="num font-medium">{v.plate}</span>
                      </button>
                      {v.label ? (
                        <span className="mt-0.5 block text-[11.5px] text-raad-muted">{v.label}</span>
                      ) : null}
                      {/* Defensive lookup (slice 24b): `no_tracker` shows no chip,
                          and an unexpected/absent value must not throw. Shared
                          with the mobile card via `InstallChip`. */}
                      <span className="mt-1 block">
                        <InstallChip v={v} />
                      </span>
                    </td>
                    <td className="py-3 pr-4 align-top">
                      <span className="block">{v.tenantName}</span>
                      <span className="mt-1 block">
                        <Pill
                          label={t(`tenants.status.${v.tenantStatus}`)}
                          tone={TENANT_TONE[v.tenantStatus]}
                        />
                      </span>
                    </td>
                    <td className="py-3 pr-4 align-top">
                      <TrackerCell v={v} isSuperAdmin={isSuperAdmin} />
                    </td>
                    <td className="py-3 pr-4 align-top">
                      <StateCell v={v} />
                    </td>
                    <td className="py-3 pr-4 align-top">
                      <LastFixCell st={st} />
                    </td>
                    <td className="py-3 pr-4 align-top">
                      <MotionCell st={st} degraded={degraded} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open !== null ? (
        <Dialog
          title={t('vehicles.detailTitle', { plate: open.plate })}
          onClose={() => setOpenId(null)}
          wide
        >
          <div className="grid gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <p className="text-[12.5px] text-raad-muted">
                {open.tenantName}
                {' · '}
                {t(`provision.planTier.${open.plan}`)}
                {open.vehicleType ? ` · ${t(`provision.type.${open.vehicleType}`)}` : ''}
              </p>
              {/* Edit the vehicle's plate / name / body type. Super-admin ONLY —
                  those are what the customer reads on every screen (the same class
                  as tenant name / plan / status), and `admin_staff` see no button
                  (absence over disabled). The customer app cannot edit these at all. */}
              {isSuperAdmin ? (
                <button
                  type="button"
                  className={SECONDARY_BUTTON}
                  onClick={() => setEditing(open.vehicleId)}
                >
                  {t('editVehicle.action')}
                </button>
              ) : null}
            </div>

            {/* 🔴 THE INSTALL RECORD, DIRECTLY ABOVE THE GATE (slice 30). Same
                component as the wizard's step 4 and the `/live` selection. */}
            <InstallationPanel vehicleId={open.vehicleId} />

            {/* 🔴 THE GAP MAHAD HIT: go-live outside the wizard. Same component
                as the wizard's step 4 and the `/live` selection. */}
            <div>
              <h3 className="mb-2 text-[13px] font-semibold">{t('vehicles.goLiveHeading')}</h3>
              <GoLivePanel
                vehicleId={open.vehicleId}
                liveAt={open.liveAt}
                hiddenAt={open.hiddenAt}
              />
            </div>

            {/* ── The tracker, and the way back out of it (18g) ───────────

                🔴 THIS PANEL IS DRIVEN BY THE LIST PAYLOAD, NEVER BY
                `GET /admin/vehicles/:id/device`. That endpoint INNER JOINs the
                open assignment, so from 18g on it answers 404 for TWO different
                states — "no such vehicle" and "a real vehicle with no tracker
                fitted" — and a screen that read it would print "vehicle not
                found" for a truck the customer can see. The list already carries
                `traccarDeviceId: number | null` and `deviceId`, which answer the
                question without the ambiguity. The API half left that 404 as a
                NAMED seam rather than changing the wire without its consumer.

                Super-admin only: `DELETE /admin/vehicles/:id/device` and the
                assignment history are both `requireSuperAdmin`, so rendering
                either for an `admin_staff` would only fetch a 403. */}
            <div>
              <h3 className="mb-2 text-[13px] font-semibold">{t('vehicles.trackerHeading')}</h3>
              {open.deviceId === null ? (
                // The maintenance case, said plainly. NOT an error, and not an
                // empty space that reads like a screen that failed to load.
                <p className="text-[12.5px] text-raad-muted">{t('vehicles.noTrackerFitted')}</p>
              ) : !isSuperAdmin ? (
                <p className="text-[12.5px] text-raad-muted">
                  {t('vehicles.unassignSuperAdminOnly')}
                </p>
              ) : (
                <>
                  <button
                    type="button"
                    className={SECONDARY_BUTTON}
                    onClick={() => setUnassigning(open.vehicleId)}
                  >
                    {t('vehicles.unassign')}
                  </button>
                  <h3 className="mb-1 mt-4 text-[13px] font-semibold">
                    {t('vehicles.deviceHistoryHeading')}
                  </h3>
                  <DeviceHistory deviceId={open.deviceId} />
                </>
              )}
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <button type="button" className={SECONDARY_BUTTON} onClick={() => setOpenId(null)}>
              {t('inventory.close')}
            </button>
          </div>
        </Dialog>
      ) : null}

      {/* Rendered OUTSIDE the row-detail Dialog's tree but inside the section, so
          it survives the parent closing and always paints on top (both are
          `fixed inset-0 z-50`; later in the DOM wins). */}
      {unassignTarget !== null ? (
        <UnassignDeviceDialog
          vehicleId={unassignTarget.vehicleId}
          plate={unassignTarget.plate}
          onClose={() => setUnassigning(null)}
        />
      ) : null}

      {/* Edit-vehicle dialog — rendered OUTSIDE the row-detail Dialog's tree (same
          reason as unassign) so it paints on top and survives the parent closing.
          Gated on `isSuperAdmin` as well as the target, so the dialog can never be
          opened by an `admin_staff` even if `editing` were somehow set. */}
      {isSuperAdmin && editTarget !== null ? (
        <EditVehicleDialog vehicle={editTarget} onClose={() => setEditing(null)} />
      ) : null}
    </section>
  );
}

export default Vehicles;
